/**
 * Shared booking vocabulary — status names, time windows, paths and the Lagos
 * date rules. Pure and dependency-free, so both Server Actions and client
 * components can import it.
 *
 * The status set and the transition table live in the database
 * (supabase/migrations/20260901000001_bookings.sql). These are the LABELS for
 * them; if you change one here, nothing breaks in the DB, but the two will
 * disagree — keep them aligned.
 */

export const BOOKING_STATUSES = [
  "requested",
  "quoted",
  "awaiting_payment",
  "confirmed",
  "completed",
  "canceled",
  "declined",
] as const;

export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export function isBookingStatus(value: unknown): value is BookingStatus {
  return (
    typeof value === "string" && (BOOKING_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Statuses waiting on the OWNER. Drives the admin-home badge.
 *
 * awaiting_payment is deliberately NOT here: it is waiting on the customer, so it
 * would be a badge the owner cannot clear. It IS open work, so it appears in the
 * queue's Open filter below.
 */
export const NEEDS_ACTION_STATUSES: BookingStatus[] = ["requested", "quoted"];

/** Statuses a customer may still cancel themselves — the UNPAID ones. Once a
 * booking is confirmed it has been paid for, and canceling means a refund, which
 * v1 routes through the owner rather than exposing to the public role. */
export const CUSTOMER_CANCELABLE_STATUSES: BookingStatus[] = [
  "requested",
  "quoted",
  "awaiting_payment",
];

/** Statuses that count as "live" — the queue's Open filter and the booking cap. */
export const OPEN_STATUSES: BookingStatus[] = [
  "requested",
  "quoted",
  "awaiting_payment",
  "confirmed",
];

export const STATUS_LABEL: Record<BookingStatus, string> = {
  requested: "Requested",
  quoted: "Quote sent",
  awaiting_payment: "Awaiting payment",
  confirmed: "Confirmed",
  completed: "Completed",
  canceled: "Canceled",
  declined: "Declined",
};

/** What the CUSTOMER should understand is happening, in their words. */
export const STATUS_CUSTOMER_HINT: Record<BookingStatus, string> = {
  requested: "We've got your request and we'll call you to talk it through.",
  quoted: "We've sent you a price. Accept it and we'll book the job in.",
  awaiting_payment:
    "Almost there — pay to lock this job in. Nothing is booked until the payment goes through.",
  confirmed: "This job is booked in. We'll see you then.",
  completed: "This job is done. Thanks for booking with us.",
  canceled: "This booking was canceled.",
  declined: "You declined this quote. Call us if you'd like a new one.",
};

/** Tailwind classes for the status chip, using the existing palette tokens. */
export const STATUS_TONE: Record<BookingStatus, string> = {
  requested: "bg-amber-50 text-amber-700 border-amber-200",
  quoted: "bg-brand-tint text-brand-dark border-brand/20",
  awaiting_payment: "bg-amber-100 text-amber-800 border-amber-300",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-slate-100 text-slate-600 border-slate-200",
  canceled: "bg-slate-100 text-slate-500 border-slate-200",
  declined: "bg-slate-100 text-slate-500 border-slate-200",
};

/**
 * How a booking reached us. 'web' is the customer booking themselves; the rest are
 * the owner entering it on their behalf. Mirrors bookings_source_check.
 */
export const BOOKING_SOURCES = ["web", "phone", "whatsapp", "walk_in"] as const;

export type BookingSource = (typeof BOOKING_SOURCES)[number];

export function isBookingSource(value: unknown): value is BookingSource {
  return typeof value === "string" && (BOOKING_SOURCES as readonly string[]).includes(value);
}

/** The ones the owner may choose. 'web' is reserved for real customer bookings. */
export const ADMIN_BOOKING_SOURCES = [
  { value: "phone", label: "Phone call" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "walk_in", label: "Walk-in" },
] as const;

export const SOURCE_LABEL: Record<BookingSource, string> = {
  web: "Online",
  phone: "Phone call",
  whatsapp: "WhatsApp",
  walk_in: "Walk-in",
};

export const TIME_WINDOWS = [
  { value: "morning", label: "Morning", hint: "8am – 12pm" },
  { value: "afternoon", label: "Afternoon", hint: "12pm – 4pm" },
  { value: "evening", label: "Evening", hint: "4pm – 8pm" },
] as const;

export type TimeWindow = (typeof TIME_WINDOWS)[number]["value"];

export function isTimeWindow(value: unknown): value is TimeWindow {
  return TIME_WINDOWS.some((w) => w.value === value);
}

export const TIME_WINDOW_LABEL: Record<TimeWindow, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
};

/** Mirrors create_booking's own bounds. Keep the two in step. */
export const BOOKING_HORIZON_DAYS = 90;

/** Field caps, mirroring the CHECK constraints so the form fails fast and kindly. */
export const FIELD_LIMITS = {
  contactName: 120,
  address: 500,
  addressMin: 5,
  area: 120,
  notes: 1000,
} as const;

export const REFERENCE_RE = /^PM-[0-9A-Z]{6}$/;

/**
 * Normalize a reference from a URL or a form. Returns null if it isn't one, so
 * junk never reaches PostgREST as a filter value.
 */
export function normalizeReference(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const ref = raw.trim().toUpperCase();
  return REFERENCE_RE.test(ref) ? ref : null;
}

/**
 * Today in Africa/Lagos as YYYY-MM-DD.
 *
 * create_booking bounds preferred_date with `(now() at time zone 'Africa/Lagos')::date`,
 * so the form's `min` must use the same clock. A UTC "today" is a day behind
 * Lagos between 23:00 and midnight WAT, which would reject a same-day booking
 * made in the evening.
 */
export function lagosToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Lagos",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** YYYY-MM-DD, `days` after the Lagos date given. Date-only, so no DST drift. */
export function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** "Fri, 12 Sep 2026" — rendered from the date parts, never from a local Date. */
export function formatBookingDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  return new Intl.DateTimeFormat("en-NG", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/** The customer's booking area. `account` is a RESERVED location slug. */
export const ACCOUNT_BOOKINGS_PATH = "/account/bookings";

export function bookingDetailPath(reference: string): string {
  return `${ACCOUNT_BOOKINGS_PATH}/${reference}`;
}

/** The booking form for a service, optionally preselecting a pricing option. */
export function bookPath(
  locationSlug: string,
  serviceSlug: string,
  optionId?: string | null,
): string {
  const base = `/${locationSlug}/services/${serviceSlug}/book`;
  return optionId ? `${base}?option=${encodeURIComponent(optionId)}` : base;
}
